import type { DeleteImpactMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  DeleteImpactDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export const useDeleteImpact = createMutationHook({
  name: 'deleteImpact',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.impact.delete.mutationOptions({ onSuccess }),
  mapInput: (variables: { Id: string }) => ({ Id: variables.Id }),
  invalidate: (trpc) => [
    trpc.frontend.impact.impacts.queryKey(),
    trpc.frontend.impact.impactById.queryKey(),
    trpc.frontend.impact.impactList.queryKey(),
    trpc.frontend.impact.impactCount.queryKey(),
    // Cascade delete removes ratings too
    trpc.frontend.impactRating.impactRatings.queryKey(),
    trpc.frontend.impactRating.impactRatingsByImpactId.queryKey(),
    trpc.frontend.impactRating.impactRatingsByRatedItemId.queryKey(),
    trpc.frontend.impactRating.impactRatingsWithAppetites.queryKey(),
    trpc.frontend.impactRating.impactRatingCount.queryKey(),
  ],
  // trpc throws if no rows are affected; the legacy shape reports a single
  // deleted impact (consumers ignore the payload).
  mapResponse: (): DeleteImpactMutation => ({
    delete_impact: {
      __typename: 'impact_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: DeleteImpactDocument,
  graphqlVariables: (variables: { Id: string }) => ({ Id: variables.Id }),
  evictFields: [
    'impact',
    'impact_rating',
    'impact_aggregate',
    'impact_rating_aggregate',
  ],
  // The impact/impactRating nav badge count queries use fetchPolicy:
  // 'no-cache', so cache eviction does not refresh them. Deleting an impact
  // cascades to its ratings, so refetch both counts.
  refetchQueries: [
    namedOperations.Query.getImpactCount,
    namedOperations.Query.getImpactRatingCount,
  ],
});
